import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircleNotch, ArrowsLeftRight, CaretDown, CaretUp } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, formatLovelace, truncMid, unixToDate } from "@/lib/format";
import { RowShareBtn, CopyBtn } from "@/components/primitives";

// ---------- latest transactions widget (home page) ----------
const useRecentTxs = (intervalMs = 20000, count = 6) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      const res = await http.get(`${API}/txs/recent`, { params: { count } });
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || { message: e.message });
    } finally {
      setLoading(false);
    }
  }, [count]);

  useEffect(() => {
    fetchRows();
    const id = setInterval(fetchRows, intervalMs);
    return () => clearInterval(id);
  }, [fetchRows, intervalMs]);

  return { rows, loading, error, refresh: fetchRows };
};

// Local (frontend-only) filters over the already-fetched rows — no extra
// round-trips, just narrows what's shown from the last `/txs/recent` poll.
const TX_FILTERS = [
  { key: "all", label: "All" },
  { key: "mint", label: "NFT mints" },
  { key: "script", label: "Smart contract" },
  { key: "stake", label: "Stake ops" },
];

const DetailKV = ({ label, value, testid }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 py-1.5 items-start" data-testid={testid}>
    <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70 mono pt-0.5">{label}</span>
    <span className="text-[13px] text-foreground/90 mono break-all">{value}</span>
  </div>
);

const Flag = ({ on, label }) => (
  <span className={cn(
    "px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.12em] mono border",
    on ? "border-[#22d3ee]/40 text-[#22d3ee] bg-[#22d3ee]/5" : "border-border/40 text-muted-foreground/50",
  )}>
    {label}: {on ? "yes" : "no"}
  </span>
);

const TxDetail = ({ r, amountMode }) => (
  <div data-testid={`latest-tx-detail-${r.tx_hash}`} className="px-4 sm:px-6 pt-1 pb-5">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-xl border border-primary/15 bg-background/40 grid-bg px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#22d3ee] mono mb-2">transaction details</div>
        <DetailKV label="Tx Hash" testid="td-hash" value={
          <span className="inline-flex items-center gap-2">{truncMid(r.tx_hash, 14, 9)}<CopyBtn value={r.tx_hash} label="tx hash" /></span>
        } />
        <DetailKV label="Block" testid="td-block" value={`#${r.block_height}`} />
        <DetailKV label="Included At" testid="td-time" value={unixToDate(r.block_time)} />
        <DetailKV label="Fee" testid="td-fee" value={formatLovelace(r.fee, amountMode)} />
        <DetailKV label="Total Output" testid="td-output" value={formatLovelace(r.output, amountMode)} />
        <DetailKV label="Size" testid="td-size" value={r.size != null ? `${r.size.toLocaleString("en-US")} bytes` : "—"} />
        <DetailKV label="Tx Index" testid="td-index" value={r.tx_index ?? "—"} />
      </div>
      <div className="rounded-xl border border-primary/15 bg-background/40 grid-bg px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#22d3ee] mono mb-2">properties</div>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="td-flags">
          <Flag on={!!r.has_mint} label="NFT mint" />
          <Flag on={!!r.has_script} label="Smart contract" />
          <Flag on={!!r.has_stake_cert} label="Stake op" />
        </div>
      </div>
    </div>
  </div>
);

export const LatestTransactions = ({ amountMode, onPickTx }) => {
  const { rows, loading, error } = useRecentTxs(20000, 6);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  // 1s tick so "Xs ago" stays current
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const filteredRows = useMemo(() => {
    if (filter === "mint") return rows.filter((r) => r.has_mint);
    if (filter === "script") return rows.filter((r) => r.has_script);
    if (filter === "stake") return rows.filter((r) => r.has_stake_cert);
    return rows;
  }, [rows, filter]);

  return (
    <div data-testid="latest-transactions" className="relative overflow-hidden border border-primary/25 bg-card/30 backdrop-blur-md rounded-2xl box-glow">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-6 h-6 shrink-0 rounded-md border border-primary/25 bg-primary/[0.06] flex items-center justify-center"
            style={{ filter: "drop-shadow(0 0 4px rgba(34,211,238,0.4))" }}
          >
            <ArrowsLeftRight size={12} className="text-[#22d3ee]" weight="bold" />
          </div>
          <span className="text-[11px] uppercase tracking-[0.25em] text-primary mono">latest transactions</span>
          {loading && rows.length === 0 ? (
            <CircleNotch size={11} className="animate-spin text-primary/70" />
          ) : null}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="hidden md:inline-flex border border-primary/15 rounded-lg overflow-hidden" data-testid="tx-filter-group">
            {TX_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`tx-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={cn(
                  "px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] mono transition-colors",
                  filter === f.key
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/20",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
            {filteredRows.length} shown · auto-refresh
          </span>
        </div>
      </div>

      {error && rows.length === 0 ? (
        <div className="px-5 py-6 text-[12px] text-muted-foreground mono">
          {error.message || "Couldn't load recent transactions."}
        </div>
      ) : rows.length === 0 && !loading ? (
        <div className="px-5 py-6 text-[12px] text-muted-foreground mono">No transactions in the recent blocks.</div>
      ) : filteredRows.length === 0 && !loading ? (
        <div data-testid="tx-filter-empty" className="px-5 py-6 text-[12px] text-muted-foreground mono">
          None of the {rows.length} recent transactions match this filter.
        </div>
      ) : (
        <>
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[520px] text-[13px] mono tabnum">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left font-sans font-medium px-5 py-2.5 border-b border-primary/10">Tx hash</th>
              <th className="text-left font-sans font-medium px-3 py-2.5 border-b border-primary/10">Date</th>
              <th className="text-left font-sans font-medium px-3 py-2.5 border-b border-primary/10">Block</th>
              <th className="text-right font-sans font-medium px-3 py-2.5 border-b border-primary/10">Fees</th>
              <th className="text-right font-sans font-medium px-3 py-2.5 border-b border-primary/10">ADA spent</th>
              <th className="text-right font-sans font-medium px-5 py-2.5 border-b border-primary/10">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(loading && rows.length === 0
              ? Array.from({ length: 6 }).map((_, i) => ({ skeleton: true, k: i }))
              : filteredRows
            ).map((r, i) =>
              r.skeleton ? (
                <tr key={`skel-${r.k}`} className="border-b border-primary/5">
                  {[0, 1, 2, 3, 4, 5].map((c) => (
                    <td key={c} className="px-3 py-3">
                      <span className="inline-block h-3 w-24 bg-primary/10 animate-pulse rounded-sm" />
                    </td>
                  ))}
                </tr>
              ) : (
                <React.Fragment key={r.tx_hash}>
                <tr
                  data-testid={`latest-tx-row-${i}`}
                  onClick={() => onPickTx?.(r.tx_hash)}
                  className={cn(
                    "border-b border-primary/5 hover:bg-primary/5 cursor-pointer transition-colors",
                    expanded === r.tx_hash && "bg-primary/[0.04]",
                  )}
                >
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="text-primary text-glow">{truncMid(r.tx_hash, 12, 8)}</span>
                      <RowShareBtn modeKey="transactions" id={r.tx_hash} />
                    </span>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">
                    {(() => {
                      const d = unixToDate(r.block_time);
                      const [day, time] = String(d).split(" ");
                      return (
                        <>
                          <div className="text-foreground/90">{day}</div>
                          {time ? <div className="text-[11px] text-muted-foreground/70">{time}</div> : null}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-3"><span className="font-heading font-normal text-[15px] text-primary text-glow whitespace-nowrap">#{r.block_height}</span></td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{formatLovelace(r.fee, amountMode)}</td>
                  <td className="px-3 py-3 text-right text-rose-400">−{formatLovelace(r.output, amountMode)}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      data-testid={`latest-tx-view-${i}`}
                      onClick={(e) => { e.stopPropagation(); setExpanded(expanded === r.tx_hash ? null : r.tx_hash); }}
                      className="inline-flex items-center justify-center h-7 w-7 border border-primary/30 rounded-md text-primary hover:bg-primary/10 transition-colors"
                      aria-label={expanded === r.tx_hash ? "Collapse transaction details" : "Expand transaction details"}
                      aria-expanded={expanded === r.tx_hash}
                    >
                      {expanded === r.tx_hash ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
                    </button>
                  </td>
                </tr>
                {expanded === r.tx_hash ? (
                  <tr className="border-b border-primary/5 bg-primary/[0.02]">
                    <td colSpan={6} className="p-0"><TxDetail r={r} amountMode={amountMode} /></td>
                  </tr>
                ) : null}
                </React.Fragment>
              ),
            )}
          </tbody>
        </table>
        </div>
        <div className="md:hidden divide-y divide-primary/5">
          {(loading && rows.length === 0
            ? Array.from({ length: 6 }).map((_, i) => ({ skeleton: true, k: i }))
            : filteredRows
          ).map((r, i) =>
            r.skeleton ? (
              <div key={`mtx-skel-${r.k}`} className="px-4 py-3 space-y-2">
                <div className="h-3 w-1/2 bg-primary/10 animate-pulse rounded-sm" />
                <div className="h-3 w-2/3 bg-primary/10 animate-pulse rounded-sm" />
              </div>
            ) : (
              <div key={r.tx_hash} className={cn(expanded === r.tx_hash && "bg-primary/[0.04]")}>
              <div
                data-testid={`latest-tx-row-m-${i}`}
                onClick={() => onPickTx?.(r.tx_hash)}
                className="px-4 py-3 cursor-pointer hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span className="text-primary text-glow break-all mono text-[13px]">{truncMid(r.tx_hash, 12, 8)}</span>
                    <RowShareBtn modeKey="transactions" id={r.tx_hash} />
                  </span>
                  <span className="font-heading font-normal text-[14px] text-primary text-glow whitespace-nowrap shrink-0">#{r.block_height}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] mono">
                  <span className="text-muted-foreground">
                    <span className="text-muted-foreground/60 uppercase tracking-wider text-[10px] mr-1">Date</span>{unixToDate(r.block_time)}
                  </span>
                </div>
                <button
                  data-testid={`latest-tx-view-m-${i}`}
                  onClick={(e) => { e.stopPropagation(); setExpanded(expanded === r.tx_hash ? null : r.tx_hash); }}
                  className="mt-2 inline-flex items-center justify-center h-7 w-7 border border-primary/30 rounded-md text-primary hover:bg-primary/10 transition-colors"
                  aria-label={expanded === r.tx_hash ? "Collapse transaction details" : "Expand transaction details"}
                  aria-expanded={expanded === r.tx_hash}
                >
                  {expanded === r.tx_hash ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
                </button>
              </div>
              {expanded === r.tx_hash ? <TxDetail r={r} amountMode={amountMode} /> : null}
              </div>
            ),
          )}
        </div>
        </>
      )}
    </div>
  );
};
